/*
 *
 * IntermediateShelfPanel
 *
 */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInjectReducer } from 'utils/injectReducer';
import { createStructuredSelector } from 'reselect';
import reducer from './reducer';
import { RootState } from './types';
import Panel from 'components/Panel';
import {
  addIntermediateShelvesOnSelectionAction,
  removeIntermediateShelvesOnSelectionAction,
  selectionIntermediateShelvesAction,
} from 'containers/IntermediateShelfPanel/actions';
import {
  makeSelectCanAddIS,
  makeSelectCommonColorsIS,
  makeSelectCounterSelectedISInShelvesSelected,
  makeSelectIsSameIntermediateShelvesInShelvesSelected,
  makeSelectISSelection,
} from 'containers/IntermediateShelfPanel/selectors';
import { makeSelectIsVisibleColorsPanel } from 'containers/Configurator/selectors/general';
import { setColorsPanelAction } from 'containers/Configurator/actions/general';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import RemoveButton from 'components/RemoveButton';
import ISPanelContent from 'containers/IntermediateShelfPanel/ISPanelContent';
import useMedia from 'use-media';
import PanelSelection from 'components/PanelSelection';
import GenericObjectSelector from 'components/GenericObjectSelector';
import { Color } from 'lib/modelBuilder/entities/Color';
import { ActivePanel } from '../Configurator/constants/general';

interface StateProps {
  isVisible: boolean;
  counterIS: number | null;
  selectedIS: number[];
  canAddIs: boolean;
  sameNumberIntermediateShelvesInSelection: boolean;
  commonColors: (Color | undefined)[] | undefined;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  isVisible: makeSelectIsVisibleColorsPanel(ActivePanel.IntermediateShelf),
  counterIS: makeSelectCounterSelectedISInShelvesSelected(),
  selectedIS: makeSelectISSelection(),
  canAddIs: makeSelectCanAddIS(),
  sameNumberIntermediateShelvesInSelection: makeSelectIsSameIntermediateShelvesInShelvesSelected(),
  commonColors: makeSelectCommonColorsIS(),
});

function IntermediateShelfPanel() {
  useInjectReducer({ key: 'intermediateShelfPanel', reducer: reducer });
  const dispatch = useDispatch();
  const {
    isVisible,
    counterIS,
    selectedIS,
    canAddIs,
    sameNumberIntermediateShelvesInSelection,
    commonColors,
  } = useSelector(stateSelector);
  const { t } = useTranslation();
  const isMobile = useMedia({ maxWidth: 1023 });

  const hidePanel = () => {
    dispatch(setColorsPanelAction());
  };

  function createISArray() {
    const selection = [];
    if (counterIS !== null) {
      let max = counterIS;
      if (!canAddIs) max = max - 1;
      for (let i = 0; i <= max; i++) {
        selection.push(i);
      }
    }
    return selection;
  }

  const listOfIS = createISArray().map(i => (
    <GenericObjectSelector
      key={i}
      title={
        counterIS !== null && i < counterIS
          ? `${t('short.Intermediate shelf')} ${i + 1}`
          : ''
      }
      color={commonColors && commonColors[i]}
      selected={selectedIS.includes(i)}
      onClick={(e: any) => {
        if (counterIS !== null && i >= counterIS) {
          dispatch(addIntermediateShelvesOnSelectionAction(undefined, i));
        } else {
          if (isMobile) {
            dispatch(selectionIntermediateShelvesAction([i]));
            dispatch(
              setColorsPanelAction(ActivePanel.IntermediateShelf, false),
            );
          } else {
            if (selectedIS.includes(i)) {
              if (e.shiftKey && selectedIS.length > 1) {
                dispatch(
                  selectionIntermediateShelvesAction(
                    selectedIS.filter(e => e !== i),
                  ),
                );
              } else {
                dispatch(selectionIntermediateShelvesAction([i]));
              }
            } else {
              if (e.shiftKey) {
                dispatch(
                  selectionIntermediateShelvesAction([...selectedIS, i]),
                );
              } else {
                dispatch(selectionIntermediateShelvesAction([i]));
              }
            }
          }
        }
      }}
      present={counterIS !== null && i < counterIS}
      canAddNew={canAddIs}
    />
  ));

  const allShelves = (
    <GenericObjectSelector
      title={t('All intermediates shelves')}
      selected={true}
      onClick={() => {
        if (isMobile) {
          dispatch(setColorsPanelAction(ActivePanel.IntermediateShelf, false));
        }
      }}
      present={true}
      canAddNew={false}
    />
  );

  /*  if (counterIS === 0) {
    if (isMobile) {
      return (
        <div className="flex justify-center w-full">
          <ISChoiceSelector />
        </div>
      );
    }
    return (
      <Panel close={hidePanel} isVisible={isVisible} autoclose={false}>
        <div className="flex justify-center py-2">
          <div className="title text-xl text-gray-900 self-center mb-2">
            {t('Add an intermediate shelf')}
          </div>
          <ISChoiceSelector />
        </div>
      </Panel>
    );
  }*/

  if (isMobile) {
    return (
      <div className="w-full">
        <div className="flex justify-center py-2">
          <div className="flex flex-col py-2">
            {/*{counterIS && counterIS <= 1 && (
              <div className="title text-sm text-gray-600 self-center mb-2">
                {t('Selection')}
              </div>
            )}*/}
            <div className="flex flex-col-reverse justify-center py-1">
              {sameNumberIntermediateShelvesInSelection ? listOfIS : allShelves}
            </div>
          </div>
        </div>

        <PanelSelection
          close={hidePanel}
          isVisible={isVisible}
          autoclose={false}
          remove={() => {
            dispatch(
              removeIntermediateShelvesOnSelectionAction(
                selectedIS,
                !sameNumberIntermediateShelvesInSelection,
              ),
            );
            hidePanel();
          }}
        >
          <ISPanelContent counterIS={counterIS} />
        </PanelSelection>
      </div>
    );
  }
  return (
    <Panel close={hidePanel} isVisible={isVisible} autoclose={false}>
      <div className="flex justify-around items-center w-full">
        <div className="flex flex-col h-full justify-center">
          <div className="flex flex-col-reverse justify-center py-1">
            {sameNumberIntermediateShelvesInSelection ? listOfIS : allShelves}
          </div>
        </div>
        {((selectedIS &&
          selectedIS.length > 0 &&
          counterIS !== null &&
          counterIS > 0) ||
          !sameNumberIntermediateShelvesInSelection) && (
          <div className="flex item-center justify-center mx-4">
            <RemoveButton
              onClick={() => {
                dispatch(
                  removeIntermediateShelvesOnSelectionAction(
                    selectedIS,
                    !sameNumberIntermediateShelvesInSelection,
                  ),
                );
                if (
                  counterIS === 1 ||
                  !sameNumberIntermediateShelvesInSelection
                ) {
                  hidePanel();
                }
              }}
            />
          </div>
        )}
        <ISPanelContent counterIS={counterIS} />
      </div>
    </Panel>
  );
}

export default IntermediateShelfPanel;

export const StyledMaterialButton = styled.button<{ selected: boolean }>`
  margin: 0 1rem;
  padding: 0.25rem 0;
  color: ${props => (!props.selected ? '#718096' : 'black')};
`;
